import React, { Fragment, lazy, Suspense, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withCookies } from 'react-cookie';
import { isMobile as isMobileDevice } from 'react-device-detect';
import { CssBaseline, Container, Box, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AppBarComponent from './layout/AppBarComponent';
//import Routes from './Routes';
import { LoginRoutes } from './Routes';
import DemoScreen from './screens/Misc/DemoScreen';
import { baseURL } from './config/axios';
import { matchPath, useNavigate, useLocation, Link } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import AppButton from './components/primitives/AppButton';
import FaIcon from './components/icons/FaIcon';
import userInfoActions from './store/actions/userInfoActions';
import CircularLoaderComponent from './components/CircularLoaderComponent';
import useStyles from './useStyles';
import useSignalR from './signalR/useSignalR';
import PopupModal from './components/PopupModal';
import VisualStepperComponent from './components/VisualStepperComponent';
import ClearLocalStorageByPath from './Utilities/ClearLocalStorageByPath';
import BottomNavigationComponent from './layout/BottomNavigationComponent';
import { Accessibility } from 'accessibility/dist/main';
import translationService from './services/translator.service';
import { AlertService, AlertsIconEnum } from './services/alerts.service';
import AfterLoginHandler from './components/AfterLoginHandler';
import { initiateAppData } from './store/SeedStore';
import languageService from './services/language.service';
import { ROUTE_TITLE_CONFIG } from './config/routeTitleConfig';
import AppTitleBand from './layout/AppTitleBand';
import { getConnectorStatusTitlePresentation } from './Utilities/connectorStatusPresentation';
import { isTokenExpired } from './Utilities/jwt';
import { AppMenuPersonalDetailsIcon } from './components/shared/common/icons/AppMenuIcons';
import plugSvg from './assets/svg/plugType2.svg';
import { secureGet } from './platform/secureStore';
import { SplashScreen } from '@capacitor/splash-screen';
import { isNative } from './platform/env';
import { bindDeepLinks } from './platform/deepLink';
import { hasBiometricOptInBeenAsked } from './platform/biometric';
import BiometricLockScreen from './components/BiometricLockScreen';
import BiometricOptInScreen from './screens/Auth/BiometricOptInScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { bindHardwareBack, bindAppStateChange } from './platform/appLifecycle';
import { addNetworkListener } from './platform/network';
import { registerForPush, bindPushHandlers, sendTokenToBackend } from './platform/push';
const Routes = lazy(() => import('./Routes'));

const PersonalDetailsRouteTitleIcon = () => (
    <AppMenuPersonalDetailsIcon size={24} primaryColor="#bcbec0" secondaryColor="#ffffff" />
);

const ChargePointRouteTitleIcon = () => (
    <img src={plugSvg} alt="" aria-hidden style={{ width: 24, height: 24, objectFit: 'contain' }} />
);

const ROUTE_TITLE_ICON_COMPONENTS = {
    personalDetails: PersonalDetailsRouteTitleIcon,
    chargePoint: ChargePointRouteTitleIcon,
};

const useTitleBandAddActionStyles = makeStyles((theme) => ({
    titleAddButton: {
        '&&': {
            gap: theme.spacing(0.25),
            minHeight: 'auto',
            paddingTop: theme.spacing(0.25),
            paddingBottom: theme.spacing(0.25),
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
        },
    },
    /** Font Awesome glyph size for title-bar Add — change `pxToRem` to adjust (e.g. 16–20). */
    titleAddIcon: {
        fontSize: theme.typography.pxToRem(14),
    },
    titleInfoButton: {
        '&&': {
            padding: theme.spacing(0.25),
            color: theme.palette.text.primary,
        },
    },
    titleInfoIcon: {
        fontSize: theme.typography.pxToRem(20),
    },
}));

const ROUTE_PATTERNS = [
    { path: '/payments', exact: true },
    { path: '/bills', exact: true },
    { path: '/myGroups', exact: true },
    { path: '/addGroup', exact: true },
    { path: '/editGroup/:id', exact: true },
    { path: '/addCar', exact: true },
    { path: '/addCharger', exact: true },
    { path: '/updateBilling', exact: true },
    { path: '/strategy/edit/:strategyId', exact: true },
    { path: '/strategy/add', exact: true },
    { path: '/strategy', exact: true },
    { path: '/locations/add', exact: true },
    { path: '/locations', exact: true },
    { path: '/ChargePointStation/Connectors/:selectedChargerId', exact: true },
    { path: '/ChargePointStation/:selectedChargerId/connector/:connectorId', exact: true },
    { path: '/ChargePointStation/:selectedChargerId', exact: true },
    { path: '/ChargePointStation', exact: true },
    { path: '/favChargers', exact: true },
    { path: '/myCars', exact: true },
    { path: '/groups/:groupId/profiles', exact: true },
    { path: '/groups/:groupId/addProfile', exact: true },
    { path: '/groups/:groupId/editProfile/:profileId', exact: true },
    { path: '/business-group/add', exact: true },
    { path: '/business-group/edit/:businessGroupId', exact: true },
    { path: '/business-group', exact: true },
    { path: '/Driver', exact: true },
    { path: '/Driver/AddDriver', exact: true },
    { path: '/Driver/:driverId', exact: true },
    { path: '/Driver/:driverId/Checklist', exact: true },
    { path: '/OperatorChargePoints', exact: true },
    { path: '/RFIDCards', exact: true },
    { path: '/RFIDCards/AddTag', exact: true },
    { path: '/settings', exact: true },
    { path: '/personal', exact: true },
    { path: '/QR', exact: true },
    { path: '/accessibility-statement', exact: true },
    { path: '/privacyPolicy', exact: true },
];

const isKnownAppRoute = (pathname) => {
    return ROUTE_PATTERNS.some((pattern) => matchPath({ path: pattern.path, end: true }, pathname));
};

function App({ userInfoReducer, languageReducer, appReducer, chargerReducer, strategyReducer, groupReducer, locationReducer, ...props }) {
    const classes = useStyles();
    const titleBandAddClasses = useTitleBandAddActionStyles();
    const { cookies } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    let { isLoggedIn } = userInfoReducer;
    let location = useLocation();
    const ourCookies = cookies.get('isLoggedIn');
    const { openPopupModal, setOpenPopupModal, setupSignalRConnection, handleReceiveMessage } = useSignalR(props);
    const [screenItem, setScreenItem] = useState(0);
    const [screenDirection, setscreenDirection] = useState('rtl');
    const [translatedData, setTranslatedData] = useState();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    
    // Auth bootstrap gate - prevents rendering Routes/LoginRoutes until auth state is determined
    // This avoids the "momentary LoginRoutes" state that can hijack valid app URLs via QR catch-all
    const [authReady, setAuthReady] = useState(false);

    // One-time biometric opt-in prompt: null = checking, false = needs prompt,
    // true = already asked (or skipped on web).
    const [biometricAsked, setBiometricAsked] = useState(null);
    useEffect(() => {
        hasBiometricOptInBeenAsked().then((v) => setBiometricAsked(v ? true : false));
    }, []);

    // The platform helpers (deep links, hardware back, push taps) were written
    // against the router-v5 history API — adapt them to v6's navigate().
    const historyAdapter = useMemo(() => ({
        push: (path) => navigate(path),
        replace: (path) => navigate(path, { replace: true }),
        goBack: () => navigate(-1),
        get location() { return { pathname: window.location.pathname }; },
        get length() { return window.history.length; },
    }), [navigate]);

    // React 18 StrictMode guard: initializeApp must run exactly once even though StrictMode
    // intentionally double-invokes effects. Without this, autoLogin and initiateAppData
    // are dispatched twice concurrently, causing race conditions and double navigation.
    const hasInitializedRef = useRef(false);

    const currentRouteTitleConfig = useMemo(() => {
        return ROUTE_TITLE_CONFIG.find((route) =>
            matchPath({ path: route.path, end: route.exact !== false }, location.pathname)
        ) || null;
    }, [location.pathname]);

    const currentRoutePageTitle = useMemo(() => {
        if (!currentRouteTitleConfig || !translatedData) return null;
        const title = translationService.getTranslation(translatedData, currentRouteTitleConfig.titleKey);
        return title || null;
    }, [currentRouteTitleConfig, translatedData]);

    const currentRouteTitleIcon = useMemo(() => {
        if (!currentRouteTitleConfig?.iconKey) return null;

        const IconComponent = ROUTE_TITLE_ICON_COMPONENTS[currentRouteTitleConfig.iconKey];
        if (!IconComponent) return null;

        return <IconComponent />;
    }, [currentRouteTitleConfig]);

    const handleGroupsHelpTitleClick = useCallback(() => {
        const groupScreenTranslations = translationService.getTranslatedData(languageReducer.language, 'addUpdateGroupScreen');
        const settings = ['', '', '', ''];
        const htmlContent = `
    <div style="direction:${languageService.getLanguageDirection(languageReducer.language)};">
        <p>${translationService.getTranslation(groupScreenTranslations, 'help.explain')}</p>
        <h4>${translationService.getTranslation(groupScreenTranslations, 'help.titles.addUpdateTitle')}</h4>
        <ul>
          ${settings.map((setting, idx) => `<li>${translationService.getTranslationAtIndex(translationService.getTranslation(groupScreenTranslations, 'help.settings'), idx)}</li>`).join('')}
        </ul>
    </div>
  `;
        AlertService.showHTMLPopUp(AlertsIconEnum.INFO, htmlContent);
    }, [languageReducer.language]);

        const handleStrategyHelpTitleClick = useCallback(() => {
        const strategyScreenTranslations = translationService.getTranslatedData(languageReducer.language, 'chargingStrategyScreen');
        const settings = translationService.getTranslation(strategyScreenTranslations, 'help.settings') || [];
        const costParts = translationService.getTranslation(strategyScreenTranslations, 'help.costParts') || [];
        const htmlContent = `
        <div style="direction:${languageService.getLanguageDirection(languageReducer.language)};">
                <p>${translationService.getTranslation(strategyScreenTranslations, 'help.explain')}</p>
                <h4>${translationService.getTranslation(strategyScreenTranslations, 'help.titles.addUpdateTitle')}</h4>
                <ul>
                    ${settings.map((setting, idx) => `<li>${translationService.getTranslationAtIndex(translationService.getTranslation(strategyScreenTranslations, 'help.settings'), idx)}</li>`).join('')}
                </ul>
                <h4>${translationService.getTranslation(strategyScreenTranslations, 'help.titles.costsTitle')}</h4>
                <ul>
                    ${costParts
                        .map((part, idx) => `<li>${translationService.getTranslationAtIndexAndProperty(translationService.getTranslation(strategyScreenTranslations, 'help.costParts'), idx, 'name')}: ${translationService.getTranslationAtIndexAndProperty(translationService.getTranslation(strategyScreenTranslations, 'help.costParts'), idx, 'explain')}</li>`)
                        .join('')}
                </ul>
        </div>
    `;
        AlertService.showHTMLPopUp(AlertsIconEnum.INFO, htmlContent);
    }, [languageReducer.language]);

    const titleBandLeadingAction = useMemo(() => {
        if (!isLoggedIn || !currentRouteTitleConfig?.titleInfoType) {
            return null;
        }

        const titleInfoHandlers = {
            groupsHelp: handleGroupsHelpTitleClick,
            strategyHelp: handleStrategyHelpTitleClick,
        };

        const onInfoClick = titleInfoHandlers[currentRouteTitleConfig.titleInfoType];
        if (!onInfoClick) {
            return null;
        }

        return (
            <IconButton
                className={titleBandAddClasses.titleInfoButton}
                onClick={onInfoClick}
                aria-label='help'
                disableRipple
            >
                <HelpOutlineIcon className={titleBandAddClasses.titleInfoIcon} />
            </IconButton>
        );
    }, [isLoggedIn, currentRouteTitleConfig, titleBandAddClasses, handleGroupsHelpTitleClick, handleStrategyHelpTitleClick]);

    const titleBandTrailingAction = useMemo(() => {
        if (!isLoggedIn || !translatedData) {
            return null;
        }

        const isUpdateBillingRoute = Boolean(matchPath({ path: '/updateBilling', end: true }, location.pathname));
        const hasPaymentMethod = Boolean(userInfoReducer?.paymentInfo);
        const addLabel = translationService.getTranslation(translatedData, 'general.actions.add');
        if (!addLabel) {
            return null;
        }

        if (isUpdateBillingRoute && !hasPaymentMethod) {
            return (
                <AppButton
                    className={titleBandAddClasses.titleAddButton}
                    onClick={() => window.dispatchEvent(new CustomEvent('update-billing-title-add-action'))}
                    icon={<FaIcon icon={faPlus} className={titleBandAddClasses.titleAddIcon} />}
                    label={addLabel}
                    ariaLabel={addLabel}
                    disableRipple
                    disableTouchRipple
                />
            );
        }

        if (!currentRouteTitleConfig?.titleAddLink) {
            return null;
        }

        return (
            <AppButton
                className={titleBandAddClasses.titleAddButton}
                component={Link}
                to={currentRouteTitleConfig.titleAddLink}
                icon={<FaIcon icon={faPlus} className={titleBandAddClasses.titleAddIcon} />}
                label={addLabel}
                ariaLabel={addLabel}
                disableRipple
                disableTouchRipple
            />
        );
    }, [isLoggedIn, currentRouteTitleConfig, translatedData, titleBandAddClasses, location.pathname, userInfoReducer]);

    const connectorDetailsRouteMatch = useMemo(() => {
        return matchPath({ path: '/ChargePointStation/:selectedChargerId/connector/:connectorId', end: true }, location.pathname);
    }, [location.pathname]);

    const connectorDetailsRouteTitle = useMemo(() => {
        if (!connectorDetailsRouteMatch || !translatedData) {
            return null;
        }

        const { selectedChargerId, connectorId } = connectorDetailsRouteMatch.params;
        const { chargersArray = [], driverChargersArray = [], publicChargersArray = [] } = chargerReducer.chargers || {};
        const allChargers = [...new Set([...driverChargersArray, ...chargersArray, ...publicChargersArray])];

        const selectedChargePoint = allChargers.find((charger) => String(charger?.id) === String(selectedChargerId));
        const selectedConnector = selectedChargePoint?.connectors?.find((connector) => String(connector?.id) === String(connectorId));

        if (!selectedConnector) {
            return null;
        }

        // spec 000220 (Part C) — the connector screen owns the 000023 waiting window; the title follows it.
        const isWaitingForCharging = String(chargerReducer.waitingForChargingConnectorId) === String(connectorId);
        return getConnectorStatusTitlePresentation(translatedData, selectedChargePoint?.status, selectedConnector?.status, { isWaitingForCharging });
    }, [connectorDetailsRouteMatch, translatedData, chargerReducer.chargers, chargerReducer.waitingForChargingConnectorId]);

    const resolvedRoutePageTitle = connectorDetailsRouteTitle?.title || currentRoutePageTitle;
    const resolvedRoutePageSubtitle = connectorDetailsRouteTitle?.subtitle || null;

    const setAccessibility = useCallback(() => {
        // Native: rely on iOS VoiceOver / Android TalkBack — the OS-provided
        // assistive layer is more capable than the JS overlay and Apple
        // explicitly recommends against custom accessibility widgets in the app.
        if (isNative) return;

        const isSecureProtocol = window.location.protocol === 'https:';
        const isLocalDevelopmentHost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

        // Keep production on HTTPS while still allowing the accessibility trigger during local development.
        if (!isSecureProtocol && !isLocalDevelopmentHost) {
            return;
        }

        var labels = {
            resetTitle: translationService.getTranslation(translatedData, 'accessibility.resetTitle'),
            closeTitle: translationService.getTranslation(translatedData, 'accessibility.closeTitle'),
            menuTitle: translationService.getTranslation(translatedData, 'accessibility.menuTitle'),
            increaseText: translationService.getTranslation(translatedData, 'accessibility.increaseText'),
            decreaseText: translationService.getTranslation(translatedData, 'accessibility.decreaseText'),
            increaseTextSpacing: translationService.getTranslation(translatedData, 'accessibility.increaseTextSpacing'),
            decreaseTextSpacing: translationService.getTranslation(translatedData, 'accessibility.decreaseTextSpacing'),
            increaseLineHeight: translationService.getTranslation(translatedData, 'accessibility.increaseLineHeight'),
            decreaseLineHeight: translationService.getTranslation(translatedData, 'accessibility.decreaseLineHeight'),
            invertColors: translationService.getTranslation(translatedData, 'accessibility.invertColors'),
            grayHues: translationService.getTranslation(translatedData, 'accessibility.grayHues'),
            underlineLinks: translationService.getTranslation(translatedData, 'accessibility.underlineLinks'),
            bigCursor: translationService.getTranslation(translatedData, 'accessibility.bigCursor'),
            readingGuide: translationService.getTranslation(translatedData, 'accessibility.readingGuide'),
            textToSpeech: translationService.getTranslation(translatedData, 'accessibility.textToSpeech'),
            speechToText: translationService.getTranslation(translatedData, 'accessibility.speechToText'),
            disableAnimations: translationService.getTranslation(translatedData, 'accessibility.disableAnimations'),
        };

        var options = {
            icon: {
                position: {
                    top: { size: 80, units: '%' },
                    left: { size: 2, units: '%' },
                    type: 'fixed',
                },
                circular: true,
                zIndex: '2147483000',
            },
            session: {
                persistent: false,
            },
            labels: labels,
        };
        new Accessibility(options);
    }, [translatedData]);

    // spec 000220 — a start/stop tap holds the loader until the connector's live status confirms it (see chargerReducer).
    const isLoading = () => {
        return userInfoReducer.stateInfo.isLoading || chargerReducer.stateInfo.isLoading || Boolean(chargerReducer.pendingChargingAction) || groupReducer.stateInfo.isLoading || strategyReducer.stateInfo.isLoading || appReducer.appLoading || locationReducer.stateInfo.isLoading;
    };

    useEffect(() => {
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;

        const initializeApp = async () => {
            baseURL();

            // Helper function to check if route is a QR code route (old or new format)
            const isQRCodeRoute = (path) => {
                // Old format: /ChargePointStation/{id}/connector/{connectorId}
                // eslint-disable-next-line no-useless-escape
                const oldFormatPattern = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/;
                // New format: /{partyID}/{uid}/{connector_id} - must have numeric uid and connector_id
                const newFormatPattern = /^\/([A-Za-z0-9]+)\/(\d+)\/(\d+)$/;
                return oldFormatPattern.test(path) || newFormatPattern.test(path);
            };

            // Token resolution: prefer Keychain (native) which survives cookie
            // wipes by the WebView, fall back to the cookie for web builds and
            // first-launch-after-update users who only have the cookie.
            let restoredToken = null;
            try {
                const fromSecure = await secureGet('authToken');
                if (fromSecure) restoredToken = fromSecure;
            } catch {
                // best-effort
            }
            if (!restoredToken && ourCookies?.token) {
                restoredToken = ourCookies.token;
            }

            if (restoredToken && !isTokenExpired(restoredToken) && !isLoggedIn) {
                // Auth bootstrap in progress - do NOT redirect, just wait for autoLogin
                try {
                    await dispatch(userInfoActions.autoLogin(restoredToken));
                    await dispatch(initiateAppData());
                } catch (error) {
                    console.error('Error during auto-login or data initialization:', error);
                    // Handle error (e.g., redirect to login)
                } finally {
                    // Auth bootstrap complete - now safe to render routes
                    setAuthReady(true);
                }
            } else {
                // No token to restore OR already logged in - auth state is already determined
                // Store the redirect URL in sessionStorage BEFORE navigation
                // This ensures the URL is saved even if we clean it by navigating to '/'
                setPostLoginRedirectOnLocalStorage(location.pathname);
                
                // Navigate to appropriate route
                const isSpecialRoute = location.pathname === '/' ||
                    location.pathname === '/signup' ||
                    location.pathname === '/forgotPassword' ||
                    location.pathname.startsWith('/resetPassword');
                
                if (isSpecialRoute) {
                    if (isLoggedIn && location.pathname !== '/') {
                        navigate('/', { replace: true });
                    } else {
                        navigate(location.pathname + location.search, { replace: true });
                    }
                } else if (isLoggedIn && isQRCodeRoute(location.pathname)) {
                    // User is already logged in and opened a QR code URL
                    // Don't navigate away - let the route handler process it
                    // The URL is already saved in sessionStorage if needed
                    // Just ensure it's preserved
                } else if (isLoggedIn && isKnownAppRoute(location.pathname)) {
                    // Valid in-app route - do not override
                } else if (!isLoggedIn && isKnownAppRoute(location.pathname)) {
                    // User is NOT logged in but on a valid app route - save for postLogin and go to login
                    // But do NOT redirect yet - let authReady gate handle this
                } else if (!isLoggedIn && isQRCodeRoute(location.pathname)) {
                    // QR code route when not logged in - save for post-login redirect
                    // Do NOT redirect to '/' - let login routes handle QR
                } else if (!isLoggedIn) {
                    // Not logged in and not a special/known route - redirect to login
                    navigate('/', { replace: true });
                }
                // else: logged in on unknown route - let Routes handle it
                
                // Auth state is already determined
                setAuthReady(true);
            }
        };

        initializeApp();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally empty - this should only run once on mount


    useEffect(() => {
        setTranslatedData(translationService.getTranslatedData(languageReducer.language));
        setscreenDirection(languageService.getLanguageDirection(languageReducer.language));
    }, [languageReducer.language]); // Remove setAccessibility from dependencies

    useEffect(() => {
        document.documentElement.setAttribute('dir', screenDirection);
    }, [screenDirection]);

    useEffect(() => {
        if (!isLoggedIn) {
            setIsUserMenuOpen(false);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (translatedData) { // Only call when translatedData exists
            setAccessibility();
        }
    }, [translatedData, setAccessibility]); // Keep setAccessibility here but add null check

    // useEffect(() => {
    //     setTranslatedData(translationService.getTranslatedData(languageReducer.language));
    //     setscreenDirection(languageService.getLanguageDirection(languageReducer.language));
    //     setAccessibility();
    // }, [languageReducer.language, setAccessibility]);

    // useEffect(() => {
    //     setAccessibility();
    // }, [translatedData, setAccessibility]);




    
    useEffect(() => {
        if (chargerReducer.chargingSummery) {
            setOpenPopupModal(true);
        }
    }, [chargerReducer.chargingSummery, setOpenPopupModal]);

    // Empty effect - intentionally left for potential future use
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {}, []);

    // Track active SignalR connection for cleanup
    const connectionRef = useRef(null);

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        // Create a local variable per effect invocation so the cleanup always
        // stops exactly the connection this effect created (React 18 StrictMode safe).
        // `cancelled` prevents the post-start invoke from firing on a connection that
        // was already stopped by cleanup (avoids "not in Connected state" console errors
        // from the StrictMode double-mount cycle).
        let cancelled = false;
        const conn = setupSignalRConnection();
        connectionRef.current = conn;

        conn.on('ReceiveMessage', (message) => handleReceiveMessage('ReceiveMessage', message));
        conn.on('UpdateConnectorStatus', (message) => handleReceiveMessage('UpdateConnectorStatus', message));
        conn.on('UpdateChargerStatus', (message) => handleReceiveMessage('UpdateChargerStatus', message));
        conn.on('UpdateTransactionData', (message) => handleReceiveMessage('UpdateTransactionData', message));
        conn.on('UpdateChargingSummery', (message) => handleReceiveMessage('UpdateChargingSummery', message));
        conn.on('ChargePointDetails', (message) => handleReceiveMessage('ChargePointDetails', message));

        conn.start()
            .then(() => {
                if (cancelled) return;
                conn.invoke('getConnectionId').catch((err) => console.error(err.toString()));
            })
            .catch((err) => {
                if (!cancelled) console.error('SignalR connection failed:', err);
            });

        return () => {
            cancelled = true;
            conn.stop().catch(() => {});
            connectionRef.current = null;
        };
    }, [isLoggedIn, setupSignalRConnection, handleReceiveMessage]);

    // --- Native (Capacitor) lifecycle wiring. All of these are no-ops on web. ---

    // Force-logout listener: the axios 401 interceptor dispatches this event
    // after clearing the secure store — end the redux session and go to login.
    useEffect(() => {
        const handler = () => {
            dispatch(userInfoActions.logout?.() ?? { type: 'USER_LOGOUT' });
            navigate('/', { replace: true });
        };
        window.addEventListener('auth:forceLogout', handler);
        return () => window.removeEventListener('auth:forceLogout', handler);
    }, [dispatch, navigate]);

    // Hide the native splash screen once auth bootstrap is done — not before,
    // otherwise users see a white WebView while autoLogin is in flight.
    useEffect(() => {
        if (!authReady || !isNative) return;
        SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    }, [authReady]);

    // Bind inbound deep links (custom adviceev:// scheme + Universal/App Links
    // once backend serves the .well-known assets). Pushes matched paths into
    // React Router so the app navigates straight to the right screen.
    useEffect(() => {
        const unbind = bindDeepLinks(historyAdapter);
        return unbind;
    }, [historyAdapter]);

    // Android hardware back. iOS uses the swipe-back gesture handled by
    // the WebView itself; this is a no-op there.
    useEffect(() => {
        return bindHardwareBack(historyAdapter, {
            rootPaths: ['/', '/ChargePointStation'],
        });
    }, [historyAdapter]);

    // SignalR resume: when the app returns from background, the WebView's
    // event loop has been paused — `withAutomaticReconnect()` may not have
    // detected the dead socket. Force a fresh start() if disconnected.
    useEffect(() => {
        return bindAppStateChange({
            onResume: () => {
                const conn = connectionRef.current;
                if (!conn) return;
                if (conn.state === 'Disconnected') {
                    conn.start().catch(() => {});
                }
            },
        });
    }, []);

    // SignalR network regain: an outage longer than the automatic-reconnect
    // window leaves the connection permanently 'Disconnected'. When
    // connectivity returns (airplane mode off, wifi/cell handoff), restart it.
    useEffect(() => {
        return addNetworkListener(({ connected }) => {
            if (!connected) return;
            const conn = connectionRef.current;
            if (conn && conn.state === 'Disconnected') {
                conn.start().catch(() => {});
            }
        });
    }, []);

    // FCM push notifications: register once after login. Handlers fire for
    // foreground delivery (ignored — SignalR already updates state), tap
    // (deep-link to the relevant screen), and token refresh (re-register).
    useEffect(() => {
        if (!isLoggedIn) return;
        let cancelled = false;
        let unbind = () => {};
        (async () => {
            const token = await registerForPush();
            if (cancelled) return;
            if (token) await sendTokenToBackend(token);
            if (cancelled) return;
            const fn = await bindPushHandlers({
                onDeepLink: (path) => {
                    if (typeof path === 'string' && path.startsWith('/')) {
                        navigate(path);
                    }
                },
                onTokenRefresh: (newToken) => {
                    if (newToken) sendTokenToBackend(newToken);
                },
            });
            if (cancelled) {
                // Effect already unmounted while we were awaiting — tear down
                // the listeners we just attached so they don't leak.
                fn?.();
            } else {
                unbind = fn;
            }
        })();
        return () => {
            cancelled = true;
            unbind();
        };
    }, [isLoggedIn, navigate]);

    ClearLocalStorageByPath();

    const isAuthRoute = (pathname) => {
        if (!pathname) return false;
        const staticAuthPaths = ['/', '/signup', '/forgotPassword'];
        if (staticAuthPaths.includes(pathname)) return true;
        if (pathname.startsWith('/resetPassword')) return true;
        const newQRPattern = /^\/([A-Za-z0-9]+)\/(\d+)\/(\d+)$/;
        if (newQRPattern.test(pathname)) return true;
        // eslint-disable-next-line no-useless-escape
        const oldQRPattern = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/;
        if (oldQRPattern.test(pathname)) return true;
        return false;
    };

    const isAuthUserShell = !isLoggedIn;
    const shouldShowAppTitleBand = Boolean(resolvedRoutePageTitle) && !(isLoggedIn && isUserMenuOpen);

    const setPostLoginRedirectOnLocalStorage = (route) => {
        // Check for old format: /ChargePointStation/:selectedChargerId/connector/:connectorId
        // Use regex to ensure it matches the exact pattern (not just contains the strings)
        // eslint-disable-next-line no-useless-escape
        const oldFormatPattern = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/;
        if (oldFormatPattern.test(route)) {
            sessionStorage.setItem('postLoginRedirect', route);
            return true; // Return true to indicate URL was saved
        }

        // Check for new QR code format: /:partyID/:uid/:connector_id
        // Pattern: /[alphanumeric]/[numeric]/[numeric]
        // Example: /LIT/80/1
        const newFormatPattern = /^\/([A-Za-z0-9]+)\/(\d+)\/(\d+)$/;
        if (newFormatPattern.test(route)) {
            sessionStorage.setItem('postLoginRedirect', route);
            return true; // Return true to indicate URL was saved
        }
        
        return false; // URL was not saved
    };

    // One-time biometric opt-in: show before anything else, ONLY on a fresh
    // install where the user hasn't yet been asked. After they tap Enable or
    // Not Now, biometricAsked flips to true and the regular app boots.
    if (biometricAsked === false) {
        return (
            <BiometricOptInScreen onDone={() => setBiometricAsked(true)} />
        );
    }

    // Show the full app for native (any platform) and for mobile browsers.
    // Desktop browsers still see the "switch to mobile" placeholder — the
    // web behavior is preserved.
    const showFullApp = isNative || isMobileDevice;

    return (
        <Fragment>
            {!showFullApp && (
                <>
                    <h1 style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}> Please switch to mobile or tablet device to use ADVICE EV APP </h1>
                    <DemoScreen />
                </>
            )}

            {showFullApp && (<>
                <CssBaseline />

                {/* Main content - only render when auth is ready */}
                {authReady && biometricAsked === true && (
                    <ErrorBoundary>
                    <BiometricLockScreen>
                    <Container disableGutters className={classes.appRoot} style={{ direction: screenDirection }}>
                        <AppBarComponent
                            className={classes.appBar}
                            cookies={props.cookies}
                            setScreenItem={setScreenItem}
                            direction={screenDirection}
                            onUserMenuStateChange={setIsUserMenuOpen}
                            hideBackButton={isAuthUserShell}
                            hideMenuButton={isAuthUserShell}
                        />

                        {shouldShowAppTitleBand ? (
                            <AppTitleBand
                                title={resolvedRoutePageTitle}
                                subtitle={resolvedRoutePageSubtitle}
                                icon={currentRouteTitleIcon}
                                leadingAction={titleBandLeadingAction}
                                trailingAction={titleBandTrailingAction}
                            />
                        ) : null}

                        <Box className={classes.appContent}>
                            {isLoggedIn ? (
                                <>
                                    <AfterLoginHandler authReady={authReady} />
                                    <Suspense fallback={null}>
                                        <Routes cookies={props.cookies} />
                                    </Suspense>
                                </>
                            ) : (
                                <LoginRoutes cookies={props.cookies} />
                            )}
                        </Box>

                        <BottomNavigationComponent className={classes.bottomNav} cookies={props.cookies} screenItem={screenItem} renderShellOnly={isAuthUserShell} />
                    </Container>
                    </BiometricLockScreen>
                    </ErrorBoundary>
                )}

                <PopupModal open={openPopupModal} setOpen={setOpenPopupModal} component={<VisualStepperComponent width='70%' options={chargerReducer.chargingSummery ? chargerReducer.chargingSummery : []} />}></PopupModal>

                {/* Single global loader - covers auth bootstrap, app data loading, and lazy route loading */}
                {(!authReady || isLoading()) && (
                    <Box className={classes.loader}>
                        <CircularLoaderComponent />
                    </Box>
                )}
            </>)}
        </Fragment>
    );
}

App.propTypes = {
    userInfoReducer: PropTypes.object.isRequired,
    appReducer: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    userInfoReducer: state.userInfoReducer,
    languageReducer: state.languageReducer,
    appReducer: state.appReducer,
    chargerReducer: state.chargerReducer,
    carsReducer: state.carsReducer,
    groupReducer: state.groupReducer,
    strategyReducer: state.strategyReducer,
    locationReducer: state.locationReducer,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withCookies(App));
